

import React, { useCallback, useEffect, useState, useMemo, useContext } from 'react';
import _ from 'underscore';
import { CouponLightboxNew, CouponBannerNew } from '@bingads-webui-react/feature-adoption-coupon';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { Constants as CCUIConstants } from '@bingads-webui-clientcenter/entity-utils';
import { FAC_FEATURE_ID, formatCoupon } from '@bingads-webui-campaign/recommendation-core';
import { withScope } from '@bingads-webui-react/scope-service';
import PropTypes from 'prop-types';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { FacCallout, couponCalloutConversionTrackingKeyMap } from '@bingads-webui-react/fac-callout';

const FAC_FEATUREID =
  parseInt(FAC_FEATURE_ID.CONVERSION_TRACKING, 10);
const FAC_STATUS_NOT_IN_PROGRESS = CCUIConstants.RecommendationAdoptionStatus.NotInProgess;
const FAC_STATUS_ACTIVE = CCUIConstants.RecommendationAdoptionStatus.Active;
const FAC_STATUS_ADOPTED = CCUIConstants.RecommendationAdoptionStatus.Adopted;

const couponRedeemFunc = (ccJsData, coupon) => () => ccJsData.FeatureAdoptionCoupon
  .update(
    coupon.id,
    {
      adoption: {
        ...coupon,
        Status: FAC_STATUS_ADOPTED,
      },
    }
  );

// Prevent bug where user might double click and try to activate same coupon twice, causing CC error.
const activateFunction = _.memoize((coupon, ccJsData) => ccJsData.FeatureAdoptionCoupon
  .update(
    coupon.id,
    {
      adoption: {
        ...coupon,
        Status: FAC_STATUS_ACTIVE,
      },
    }
  ), ({ id }) => id);

const ConversionTrackingCouponUnstyled = (props) => {
  const [showCouponLightbox, setShowCouponLightbox] = useState(false);
  const [showCouponBanner, setShowCouponBanner] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [couponLocalized, setCouponLocalized] = useState(null);
  const instrumentation = useContext(InstrumentationContext);
  const { activity } = useMemo(() => instrumentation.createScenarioAndActivity({
    scenarioName: 'ConversionTrackingCoupon',
    activityName: 'CouponRender',
  }), [instrumentation]);

  const { couponAdoptedCallback, serverContext } = props;

  const { DynamicPermissions } = serverContext;

  const onActivateCoupon = useCallback(() => activateFunction(coupon, props.ccJsData).then(() => {
    const newCoupon = {
      ...coupon,
      Status: FAC_STATUS_ACTIVE,
    };
    setCoupon(newCoupon);
  }).catch(() => {
    setShowCouponLightbox(false);
    notificationApi.showWarning(props.i18n.getString(_TL_('Only Advertiser Campaign Managers, Super Admins, and Standard Users have permission to redeem coupons. Please use a different account or contact this account’s administrator to adjust your user role accordingly.')));
  }), [coupon, props.ccJsData, props.i18n]);

  useEffect(() => {
    props.ccJsData.FeatureAdoptionCoupon
      .findAll({ aid: props.scope.values.aid })
      .catch((err) => {
        // It seems this returns 401 sometimes, which might just be user token expiring. Do just trace for now to see what issues are here.
        activity.trace(`Got following response when fetching FACs: ${JSON.stringify(err)}`);
        return [];
      })
      .then(coupons => _.filter(
        coupons,
        ({ Status, FeatureId }) => FeatureId === FAC_FEATUREID && (Status === FAC_STATUS_NOT_IN_PROGRESS || Status === FAC_STATUS_ACTIVE)
      )).then((coupons) => {
        if (coupons.length > 0) {
          const serverCoupon = coupons[0];
          setCoupon(serverCoupon);
          if (serverCoupon.Status === FAC_STATUS_ACTIVE) {
            couponAdoptedCallback(couponRedeemFunc(props.ccJsData, serverCoupon));
          }
        }
      });
  }, [props.isCouponAdopted, props.ccJsData.FeatureAdoptionCoupon, props.scope.values.aid, couponAdoptedCallback, props.ccJsData, props.i18n, serverContext.CurrentAccount.CurrencyCode, coupon, activity]);

  const i18n = useMemo(() => buildLegacyI18n(props.i18n, [couponCalloutConversionTrackingKeyMap]), [props.i18n]);

  useEffect(() => {
    if (!coupon) {
      return;
    }
    const { Status } = coupon;
    setCouponLocalized(formatCoupon({ i18n, coupon, currency: serverContext.CurrentAccount.CurrencyCode }));
    setShowCouponBanner(Status === FAC_STATUS_ACTIVE);
    setShowCouponLightbox(Status === FAC_STATUS_NOT_IN_PROGRESS || (DynamicPermissions.FacCalloutRevamp && Status === FAC_STATUS_ACTIVE));
  }, [DynamicPermissions.FacCalloutRevamp, coupon, i18n, serverContext.CurrentAccount.CurrencyCode]);

  return (
    <React.Fragment>
      {showCouponLightbox &&
        <>
          {DynamicPermissions.FacCalloutRevamp
            ? <FacCallout coupon={couponLocalized} i18n={i18n} onActivate={onActivateCoupon} onCancel={onActivateCoupon} userPreferenceService={props.ccJsData.UserPreference} />
            : <CouponLightboxNew coupon={couponLocalized} i18n={i18n} onSubmit={onActivateCoupon} onCancel={onActivateCoupon} />
          }
        </>
      }
      {showCouponBanner &&
        <CouponBannerNew i18n={i18n} coupon={couponLocalized} titleKey="FAC_Coupon_Banner_Title" messageKey="Coupon_LightboxTitleNew" />}
    </React.Fragment>
  );
};

ConversionTrackingCouponUnstyled.propTypes = {
  scope: PropTypes.shape({
    values: PropTypes.shape({
      aid: PropTypes.number.isRequired,
    }),
  }).isRequired,
  ccJsData: PropTypes.shape({
    FeatureAdoptionCoupon: PropTypes.shape({
      findAll: PropTypes.func,
      update: PropTypes.func,
    }),
    UserPreference: PropTypes.shape({}),
  }).isRequired,
  couponAdoptedCallback: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      CurrencyCode: PropTypes.string,
    }),
    DynamicPermissions: PropTypes.shape({
      FacCalloutRevamp: PropTypes.bool,
    }),
  }).isRequired,
  isCouponAdopted: PropTypes.bool,
};

ConversionTrackingCouponUnstyled.defaultProps = {
  couponAdoptedCallback: _.noop,
  isCouponAdopted: false,
};

export const ConversionTrackingCoupon = withServerContext(withI18nConsumer(withScope(ConversionTrackingCouponUnstyled)));
